import type { ColorInput, TinyColor as TinyColorType } from "@ctrl/tinycolor";
import TinyColor, { Numberify, RGBA } from "@ctrl/tinycolor";
import { memo, useState } from "react";
import {
  Alpha,
  EditableInput,
  Hue,
  Saturation,
} from "react-color/lib/components/common";
import { Flex } from "../../flex";
import { RedoButton } from "../buttons/redo-button";
import * as colorPickerCSS from "./redo-color-picker.module.css";

export const formatColorOutput = (
  color: TinyColorType,
  showAlpha: boolean,
): string => {
  // Only use hex8 format when showAlpha is true AND transparency < FF (alpha < 1.0)
  // The practical reason is because Outlook for Windows does not support hex8 format
  // and will render it as fully transparent.
  return showAlpha && color.getAlpha() < 1.0
    ? color.toHex8String()
    : color.toHexString();
};

export const RedoColorPicker = memo(function RedoColorPicker({
  value,
  onChange,
  showAlpha,
}: {
  value: string;
  onChange: (val: string) => void;
  showAlpha?: boolean;
}) {
  const tinycolor = TinyColor(value);
  const hsl = tinycolor.toHsl();
  const hsv = tinycolor.toHsv();
  const rgb = tinycolor.toRgb();

  const saturationPointer = () => {
    return (
      <div
        className={colorPickerCSS.saturationPointer}
        style={{ backgroundColor: value }}
      />
    );
  };
  const hueAndAlphaPointer = () => {
    return (
      <div
        className={colorPickerCSS.hueAndAlphaPointer}
        style={{ backgroundColor: value }}
      />
    );
  };

  const handleChange = (data: ColorInput) => {
    const color = TinyColor(data);
    if (!color.isValid) {
      return;
    }
    const hex = formatColorOutput(color, !!showAlpha);
    onChange(hex);
  };

  return (
    <div className={colorPickerCSS.picker}>
      <div className={colorPickerCSS.saturation}>
        <Saturation
          {...{ hsl, hsv }}
          className={colorPickerCSS.Saturation}
          color={value}
          onChange={handleChange as any} // Do not listen to the type error here, react-color lies about what it passes into this callback
          pointer={saturationPointer}
        />
      </div>
      <div className={colorPickerCSS.controls}>
        <div className={colorPickerCSS.sliders}>
          <div className={colorPickerCSS.hue}>
            <Hue
              {...{ hsl }}
              className={colorPickerCSS.Hue}
              color={value}
              onChange={handleChange as any} // Do not listen to the type error here, react-color lies about what it passes into this callback
              pointer={hueAndAlphaPointer}
            />
          </div>
          {showAlpha && (
            <div className={colorPickerCSS.alpha}>
              <Alpha
                {...{ hsl, rgb }}
                className={colorPickerCSS.Alpha}
                color={value}
                onChange={handleChange as any} // Do not listen to the type error here, react-color lies about what it passes into this callback
                pointer={hueAndAlphaPointer}
              />
            </div>
          )}
        </div>
      </div>
      <Flex dir="column" gap="md">
        <ColorPickerInputs
          onChange={handleChange}
          rgb={rgb}
          showAlpha={showAlpha}
          value={value}
        />
        <RedoButton
          hierarchy={value === "transparent" ? "primary" : "secondary"}
          onClick={() => {
            if (showAlpha) {
              // Keep current RGB, set opacity to 0
              const transparentWithCurrentRGB = {
                r: rgb.r,
                g: rgb.g,
                b: rgb.b,
                a: 0,
              };
              handleChange(transparentWithCurrentRGB);
            } else {
              handleChange("transparent");
            }
          }}
          size="sm"
          text="Transparent"
        />
      </Flex>
    </div>
  );
});

const ColorPickerInputs = memo(function ColorPickerInputs({
  value,
  rgb,
  onChange,
  showAlpha,
}: {
  value: string;
  rgb: Numberify<RGBA>;
  onChange: (data: ColorInput) => void;
  showAlpha?: boolean;
}) {
  const [colorFormat, setColorFormat] = useState<"hex" | "rgb">("hex");

  const handleSingleChange = (r: number, g: number, b: number, a: number) => {
    const colorData = {
      r: Math.min(255, Math.max(0, r)),
      g: Math.min(255, Math.max(0, g)),
      b: Math.min(255, Math.max(0, b)),
      a: Math.min(100, Math.max(0, a)) / 100,
    };
    onChange(colorData);
  };

  return (
    <div className={colorPickerCSS.controls}>
      <div className={colorPickerCSS.formatSelector}>
        <select
          className={colorPickerCSS.formatDropdown}
          onChange={(e) => setColorFormat(e.target.value as "hex" | "rgb")}
          value={colorFormat}
        >
          <option value="hex">HEX</option>
          <option value="rgb">RGB</option>
        </select>
      </div>

      {colorFormat === "hex" ? (
        <div className={colorPickerCSS.hexInput}>
          <EditableInput
            onChange={onChange as any} // Do not listen to the type error here, react-color lies about what it passes into this callback
            value={(() => {
              const color = TinyColor(value);
              return color.isValid
                ? formatColorOutput(color, !!showAlpha)
                : value;
            })()}
          />
        </div>
      ) : (
        <div className={colorPickerCSS.rgbaSection}>
          <div className={colorPickerCSS.rgba}>
            <input
              max="255"
              min="0"
              onChange={(e) =>
                handleSingleChange(
                  Number(e.target.value) || 0,
                  rgb.g,
                  rgb.b,
                  rgb.a,
                )
              }
              type="number"
              value={String(rgb.r)}
            />
          </div>
          <div className={colorPickerCSS.rgba}>
            <input
              max="255"
              min="0"
              onChange={(e) =>
                handleSingleChange(
                  rgb.r,
                  Number(e.target.value) || 0,
                  rgb.b,
                  rgb.a,
                )
              }
              type="number"
              value={String(rgb.g)}
            />
          </div>
          <div className={colorPickerCSS.rgba}>
            <input
              max="255"
              min="0"
              onChange={(e) =>
                handleSingleChange(
                  rgb.r,
                  rgb.g,
                  Number(e.target.value) || 0,
                  rgb.a,
                )
              }
              type="number"
              value={String(rgb.b)}
            />
          </div>
          {showAlpha && (
            <div className={colorPickerCSS.rgba}>
              <div className={colorPickerCSS.alphaInputContainer}>
                <input
                  max="100"
                  min="0"
                  onChange={(e) =>
                    handleSingleChange(
                      rgb.r,
                      rgb.g,
                      rgb.b,
                      Number(e.target.value) || 0,
                    )
                  }
                  type="number"
                  value={String(Math.round(rgb.a * 100))}
                />
                <span className={colorPickerCSS.percentageLabel}>%</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});
